import React from "react";
import Link from "next/link";
import { LearnLayout } from "@/components/layout/LearnLayout";
import { Zap, CheckCircle, BookOpen, Clock, Target, AlertTriangle } from "lucide-react";
import { useUniversityContent } from "@/lib/university/useUniversityContent";
import { getUniversityPack } from "@/lib/universityPack";

export default function ExamTechnique() {
    const content = useUniversityContent();
    const pack = getUniversityPack();
    const guideData = content.guides.exam_technique;

    return (
        <LearnLayout
            title={guideData.seo_title}
            description={guideData.seo_desc}
            slug="exam-technique"
            relatedArticles={[
                { title: "Academic Integrity", slug: "academic-integrity" },
                { title: "AI Study Assistant", slug: "ai-study-assistant" },
            ]}
        >
            <div className="prose prose-purple max-w-none">
                <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 transition-colors">
                    {guideData.title}
                </h1>

                <div className="flex flex-wrap gap-2 text-sm not-prose mb-8">
                    <span className="px-3 py-1 bg-purple-50 rounded-full border border-purple-200 text-purple-700 font-medium">
                        11 min read
                    </span>
                    <span className="px-3 py-1 bg-purple-50 rounded-full border border-purple-200 text-purple-700 font-medium">
                        Exam Prep
                    </span>
                </div>

                {/* Dynamic Tips/Policy Section */}
                {guideData.policy_section && (
                    <div className="not-prose my-10 p-8 bg-purple-50 border border-purple-100 rounded-2xl">
                        <h3 className="text-xl font-bold text-purple-900 mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-purple-600" />
                            {guideData.policy_section.title}
                        </h3>
                        <ul className="space-y-3">
                            {guideData.policy_section.content.map((point, i) => (
                                <li key={i} className="flex gap-3 text-purple-800 text-sm font-medium">
                                    <CheckCircle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
                    The IRAC Method for Problem Questions
                </h2>
                <p>
                    <strong>IRAC = Issue, Rule, Application, Conclusion</strong>
                </p>

                <h3>Step 1: Issue Identification</h3>
                <p>
                    <strong>Example</strong>: "Does Anna have a valid claim in
                    negligence against Beta Ltd?"
                </p>

                <h3>Step 2: Rule Statement</h3>
                <p>
                    <strong>Example</strong>: "Where the situation falls within an
                    established category, duty of care follows existing authority.
                    Where it is genuinely novel, the court reasons incrementally by
                    analogy with decided cases (Robinson v Chief Constable of West
                    Yorkshire Police [2018] UKSC 4), having regard to foreseeability,
                    proximity, and whether it is fair, just and reasonable to impose a
                    duty (Caparo Industries plc v Dickman [1990] 2 AC 605)."
                </p>

                <h3>Step 3: Application to Facts</h3>
                <p>
                    <strong>Example</strong>: "Harm was foreseeable because Beta Ltd
                    knew the pavement was icy..."
                </p>

                <h3>Step 4: Conclusion</h3>
                <p>
                    <strong>Example</strong>: "Therefore, a duty of care likely exists..."
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
                    Time Management Under Exam Conditions
                </h2>
                <p>
                    <strong>Why planning matters</strong>: 5 minutes planning saves 15
                    minutes rewriting a muddled answer.
                </p>
                <div className="not-prose my-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex items-start gap-4">
                        <Clock className="w-6 h-6 text-gray-400 mt-1" />
                        <div>
                            <h4 className="font-bold text-gray-900 mt-0 mb-2">The Golden Rule</h4>
                            <p className="text-gray-600 m-0 text-sm">
                                Never start writing until you have a plan. For a 60-minute question, spend 10-15 minutes planning.
                            </p>
                        </div>
                    </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
                    Revision Strategies
                </h2>
                <p>
                    <strong>Active Recall &gt; Passive Re-Reading</strong>.
                    Closing your book and writing out the ratio from memory is far more effective than reading cases for the 5th time.
                </p>
                <p>
                    <strong>Tool</strong>: {content.brand.name} can quiz you: "Test me on the elements of negligence".
                </p>

                {/* CTA */}
                <div className="not-prose my-12 p-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl text-white">
                    <h3 className="text-2xl font-bold mb-3">
                        Ace Your Next {pack.display_name} Exam
                    </h3>
                    <p className="text-purple-100 mb-6">
                        {content.brand.name} helps you master case law and build exam confidence.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            href="/login"
                            className="inline-block px-6 py-3 bg-white text-purple-600 font-bold rounded-lg hover:bg-gray-100 transition"
                        >
                            Get Started
                        </Link>
                    </div>
                </div>

            </div>
        </LearnLayout>
    );
}

